import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { renderStore } from './render-tracker';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface SummaryRow {
  component: string;
  mounts: number;
  updates: number;
  initRenders: number;
  interactionRenders: number;
  total: number;
}

export function RenderDebugPanel() {
  const [collapsed, setCollapsed] = useState(true);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSummary(renderStore.getSummary());
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleReset = useCallback(() => {
    renderStore.reset();
    setSummary([]);
  }, []);

  const handlePrint = useCallback(() => {
    renderStore.printSummary();
    renderStore.printDetailed();
  }, []);

  const totalRenders = summary.reduce((s, r) => s + r.total, 0);

  const content = collapsed ? (
    <button
      onClick={() => setCollapsed(false)}
      style={{
        position: 'fixed', bottom: 80, right: 8, zIndex: Z_INDEX.debug,
        background: '#FF5722', color: '#fff', border: 'none', borderRadius: '50%',
        width: 44, height: 44, fontSize: 14, fontWeight: 'bold', cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        WebkitTapHighlightColor: 'transparent',
      }}
      title="Render Debug Panel"
    >
      {totalRenders}
    </button>
  ) : (
    <div style={{
      position: 'fixed', bottom: 80, right: 8, zIndex: Z_INDEX.debug,
      background: '#1a1a2e', color: '#e0e0e0', borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)', fontFamily: 'monospace', fontSize: 11,
      width: 'min(420px, calc(100vw - 16px))', maxHeight: '50vh', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 10px', borderBottom: '1px solid #333', flexShrink: 0,
      }}>
        <span style={{ fontWeight: 'bold', color: '#FF5722' }}>
          Render Tracker ({totalRenders})
        </span>
        <span style={{ color: '#888', fontSize: 10 }}>
          phase: {renderStore.currentAppPhase}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={handleReset} style={btnStyle}>Reset</button>
          <button onClick={handlePrint} style={btnStyle}>Console</button>
          <button onClick={() => setCollapsed(true)} style={btnStyle}>_</button>
        </div>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #333', position: 'sticky', top: 0, background: '#1a1a2e' }}>
              <th style={thStyle}>Component</th>
              <th style={thStyle}>Mt</th>
              <th style={thStyle}>Upd</th>
              <th style={thStyle}>Init</th>
              <th style={thStyle}>Int</th>
              <th style={thStyle}>Tot</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((row) => (
              <tr key={row.component} style={{ borderBottom: '1px solid #222' }}>
                <td style={{ ...tdStyle, color: row.total > 10 ? '#FF5722' : row.total > 5 ? '#FFC107' : '#4CAF50' }}>
                  {row.component}
                </td>
                <td style={tdStyle}>{row.mounts}</td>
                <td style={tdStyle}>{row.updates}</td>
                <td style={tdStyle}>{row.initRenders}</td>
                <td style={tdStyle}>{row.interactionRenders}</td>
                <td style={{ ...tdStyle, fontWeight: 'bold' }}>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {summary.length === 0 && (
          <div style={{ padding: 12, textAlign: 'center', color: '#666' }}>No renders recorded</div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

const btnStyle: React.CSSProperties = {
  background: '#333', color: '#ccc', border: '1px solid #555', borderRadius: 3,
  padding: '2px 6px', cursor: 'pointer', fontSize: 10,
};

const thStyle: React.CSSProperties = {
  padding: '4px 6px', textAlign: 'left', fontSize: 10, color: '#888',
};

const tdStyle: React.CSSProperties = {
  padding: '3px 6px', fontSize: 11,
};
